import { WEAPONS } from '@fisura/sim';
import { TEAM_COLOR } from '@fisura/shared';
import { RosterFlag, type MatchSummaryMsg, type MatchSummaryRow } from '@fisura/protocol';

const MONO = 'ui-monospace, "SF Mono", Menlo, monospace';
const VERSION = '0.0.2-PROTO';
// Short weapon tag shown after each player name.
const WEAPON_TAG: Record<string, string> = {
  SMG: 'SMG',
  Shotgun: 'SG',
  Rifle: 'RIF',
  Rocket: 'RPG',
};

const css = (c: [number, number, number]): string =>
  `rgb(${Math.round(c[0] * 255)},${Math.round(c[1] * 255)},${Math.round(c[2] * 255)})`;

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function mmss(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function weaponTag(weaponId: number): string {
  const name = WEAPONS[weaponId]?.name ?? '';
  return WEAPON_TAG[name] ?? name.slice(0, 3).toUpperCase();
}

/**
 * Full-screen end-of-round leaderboard, rendered from a frozen MatchSummary.
 * Built to match the reference mockup: mode/map header, a centered winner
 * banner, two team panels (Police left, Fisuras right) ranked by score, a
 * MATCH STATS strip, and the "returning to menu" countdown. Pure DOM overlay
 * over the (frozen) 3D scene — no pointer events, so pointer lock survives.
 */
export class EndBoard {
  private readonly root: HTMLDivElement;
  private readonly countdownEl: HTMLSpanElement;
  private readonly onlineEl: HTMLDivElement;
  private shown: MatchSummaryMsg | null = null;

  constructor() {
    this.root = document.createElement('div');
    Object.assign(this.root.style, {
      position: 'fixed',
      inset: '0',
      zIndex: '16',
      display: 'none',
      pointerEvents: 'none',
      fontFamily: MONO,
      color: '#e8e8ec',
      background: 'linear-gradient(rgba(8,9,12,0.82), rgba(8,9,12,0.9))',
      padding: '2.2vh 2.4vw',
      boxSizing: 'border-box',
    } as Partial<CSSStyleDeclaration>);
    // Two named slots updated every frame; the rest is rebuilt only on new data.
    this.countdownEl = document.createElement('span');
    this.onlineEl = document.createElement('div');
    document.body.appendChild(this.root);
  }

  hide(): void {
    if (this.root.style.display !== 'none') this.root.style.display = 'none';
    this.shown = null;
  }

  /** Show (rebuild only when the summary object changes). */
  show(summary: MatchSummaryMsg | null, myName: string | null, playersOnline: number): void {
    if (!summary) {
      this.hide();
      return;
    }
    this.root.style.display = 'block';
    if (this.shown !== summary) {
      this.shown = summary;
      this.render(summary, myName);
    }
    this.onlineEl.textContent = `PLAYERS ONLINE: ${playersOnline}`;
  }

  /** Per-frame: refresh just the "returning to menu" countdown. */
  updateCountdown(msLeft: number): void {
    this.countdownEl.textContent = mmss(msLeft);
  }

  private render(s: MatchSummaryMsg, myName: string | null): void {
    const fisuraC = css(TEAM_COLOR[0] ?? [0.95, 0.42, 0.12]);
    const policeC = css(TEAM_COLOR[1] ?? [0.25, 0.4, 0.95]);
    const winnerName = s.winnerTeam === 0 ? 'FISURAS' : s.winnerTeam === 1 ? 'POLICE' : 'DRAW';
    const winnerC = s.winnerTeam === 0 ? fisuraC : s.winnerTeam === 1 ? policeC : '#cfcfd6';
    const accPct = s.totalShots > 0 ? Math.round((s.totalHits / s.totalShots) * 100) : 0;

    this.root.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:flex-start">
        <div>
          <div style="font-size:30px;font-weight:700;letter-spacing:2px">TEAM DEATHMATCH</div>
          <div style="font-size:22px;color:#9a9aa2;letter-spacing:2px;margin-top:2px">RIVADAVIA BATTLE</div>
        </div>
        <div style="text-align:center;border:1px solid #2a2a32;border-radius:8px;padding:10px 26px;background:rgba(0,0,0,0.35)">
          <div style="font-size:20px;font-weight:700;letter-spacing:3px;color:${winnerC}">${winnerName}${s.winnerTeam <= 1 ? ' WON' : ''}</div>
          <div style="font-size:34px;font-weight:700;margin-top:2px">
            <span style="color:${policeC}">${s.score1}</span>
            <span style="color:#666;margin:0 10px">:</span>
            <span style="color:${fisuraC}">${s.score0}</span>
          </div>
        </div>
        <div style="text-align:right">
          <div style="font-size:22px;font-weight:700;letter-spacing:1px;color:#cfcfd6">
            NEXT GAME STARTS IN <span id="eb-count" style="color:#ff7a1a">0:00</span>
          </div>
          <div style="font-size:12px;color:#9a9aa2;letter-spacing:1px;margin-top:6px">MATCH ID <span style="color:#cfcfd6">${escapeHtml(s.matchId)}</span></div>
        </div>
      </div>

      <div style="display:flex;gap:2vw;margin-top:2.4vh">
        ${this.panel(s, 1, 'POLICE', policeC, s.score1, myName)}
        ${this.panel(s, 0, 'FISURAS', fisuraC, s.score0, myName)}
      </div>

      <div style="margin-top:2.2vh;border:1px solid #20212a;border-radius:8px;padding:14px 20px;background:rgba(0,0,0,0.3)">
        <div style="font-size:12px;letter-spacing:3px;color:#9a9aa2;margin-bottom:10px">MATCH STATS</div>
        <div style="display:flex;justify-content:space-between;gap:1vw">
          ${this.stat('◎', `${accPct}%`, 'ACCURACY', `(${s.totalHits} / ${s.totalShots})`)}
          ${this.stat('☠', String(s.totalKills), 'TOTAL KILLS')}
          ${this.stat('☠', String(s.totalDeaths), 'TOTAL DEATHS')}
          ${this.stat('✚', String(s.totalAssists), 'ASSISTS')}
          ${this.stat('★', String(s.longestStreak), 'LONGEST KILLSTREAK', escapeHtml(s.longestStreakName))}
          ${this.stat('◷', mmss(s.durationMs), 'MATCH DURATION')}
        </div>
      </div>

      <div style="position:absolute;left:2.4vw;bottom:2vh;font-size:11px;color:#6a6a72;line-height:1.6">
        <div id="eb-online"></div>
        <div>VERSION: ${VERSION}</div>
      </div>
      <div style="position:absolute;right:2.4vw;bottom:2vh;font-size:11px;color:#6a6a72;letter-spacing:2px">PHASE 0 PROTOTYPE</div>
    `;

    // Re-bind the per-frame slots to the freshly-rendered nodes.
    const c = this.root.querySelector('#eb-count');
    if (c) c.replaceWith(this.countdownEl);
    const o = this.root.querySelector('#eb-online');
    if (o) o.replaceWith(this.onlineEl);
    Object.assign(this.countdownEl.style, { color: '#ff7a1a' });
  }

  private panel(
    s: MatchSummaryMsg,
    teamId: number,
    label: string,
    color: string,
    total: number,
    myName: string | null,
  ): string {
    const rows = s.rows
      .filter((r) => r.teamId === teamId)
      .sort((a, b) => b.score - a.score || b.kills - a.kills || a.deaths - b.deaths)
      .map((r, i) => this.row(r, i + 1, color, myName))
      .join('');
    return `
      <div style="flex:1;min-width:0">
        <div style="display:flex;justify-content:space-between;align-items:baseline;border-bottom:2px solid ${color};padding-bottom:6px">
          <span style="font-size:20px;font-weight:700;letter-spacing:2px;color:${color}">${label}</span>
          <span style="font-size:20px;font-weight:700;color:${color}">${total}</span>
        </div>
        <table style="width:100%;border-collapse:collapse;font-size:13px;margin-top:6px">
          <tr style="color:#83838c;font-size:11px;letter-spacing:1px">
            <th style="text-align:left;padding:3px 6px;width:22px">#</th>
            <th style="text-align:left;padding:3px 6px">PLAYER</th>
            <th style="text-align:right;padding:3px 8px">SCORE</th>
            <th style="text-align:right;padding:3px 8px">KILLS</th>
            <th style="text-align:right;padding:3px 8px">DEATHS</th>
            <th style="text-align:right;padding:3px 8px">ASSISTS</th>
            <th style="text-align:right;padding:3px 6px">K/D</th>
          </tr>
          ${rows}
        </table>
      </div>`;
  }

  private row(r: MatchSummaryRow, rank: number, color: string, myName: string | null): string {
    const kd = r.deaths > 0 ? (r.kills / r.deaths).toFixed(2) : r.kills.toFixed(2);
    const me = myName !== null && r.name === myName;
    void RosterFlag; // (bot flag intentionally not shown on this board, per the mockup)
    const tag = `<span style="color:#6a6a72"> [${weaponTag(r.weaponId)}]</span>`;
    return `
      <tr style="${me ? 'background:rgba(255,255,255,0.08)' : ''}">
        <td style="padding:3px 6px;color:#6a6a72">${rank}</td>
        <td style="padding:3px 6px;color:${color}">${escapeHtml(r.name)}${tag}</td>
        <td style="padding:3px 8px;text-align:right;font-weight:700">${r.score.toLocaleString()}</td>
        <td style="padding:3px 8px;text-align:right">${r.kills}</td>
        <td style="padding:3px 8px;text-align:right;color:#9a9aa2">${r.deaths}</td>
        <td style="padding:3px 8px;text-align:right;color:#9a9aa2">${r.assists}</td>
        <td style="padding:3px 6px;text-align:right">${kd}</td>
      </tr>`;
  }

  private stat(icon: string, value: string, label: string, sub = ''): string {
    return `
      <div style="display:flex;align-items:center;gap:10px;flex:1">
        <span style="font-size:24px;color:#83838c">${icon}</span>
        <div>
          <div style="font-size:20px;font-weight:700">${value}</div>
          <div style="font-size:10px;letter-spacing:1px;color:#83838c">${label}</div>
          ${sub ? `<div style="font-size:10px;color:#6a6a72">${sub}</div>` : ''}
        </div>
      </div>`;
  }
}
